import { useMapRelativePositionToCameraPosition } from '@sketch-hq/sketch-web-renderer'
import {
  InspectorLayersTreeNode,
  useInspectorContext,
} from '../../hooks/inspector'
import { useMemo } from 'react'

/**
 * Take as an input a position relative to the canvas html element (where the right click happened)
 * and returns the tree of layers containing the canvas point behind the right click.
 * Note that the point can overlap with layers from different branches of the tree.
 */
export function useSubLayersTreeAtRelativePosition(
  relativePosition: { x: number; y: number } | null
) {
  const { layersTreeRootNode } = useInspectorContext()
  const mapRelativePositionToCameraPosition = useMapRelativePositionToCameraPosition()

  return useMemo(() => {
    if (!layersTreeRootNode || !relativePosition) {
      return null
    }
    const cameraPosition = mapRelativePositionToCameraPosition(
      relativePosition.x,
      relativePosition.y
    )
    if (!cameraPosition) {
      return null
    }
    return getLayersTreeContainingPoint(cameraPosition, layersTreeRootNode)
  }, [
    layersTreeRootNode,
    mapRelativePositionToCameraPosition,
    relativePosition,
  ])
}

/**
 * Create a new tree with only the layers that contain the point.
 */
function getLayersTreeContainingPoint(
  point: { x: number; y: number },
  layersTreeRootNode: InspectorLayersTreeNode
): InspectorLayersTreeNode {
  /**
   * Function called recursively to get the list of layers that contain the point and that should be used as children of
   * the parent node. The general idea is that we go through the tree nodes and create a new tree
   * of layers, only keeping nodes for which the bounds of the layer contain the point.
   *
   * When discarding/skipping a node we make sure to attach it's potential descendants layers to the parent.
   * Note: If we were not skipping any nodes, the function would always return a list of one node but when skipping
   * a node, we may replace it with a list of descendent nodes instead of the node itself.
   */
  function getLayersListToUseAsChildren(
    /**
     * This is the node is used as a part of the original tree
     * we may want to keep it or not in the layers tree.
     */
    currentLayerTreeNode: InspectorLayersTreeNode
  ): InspectorLayersTreeNode[] {
    const shouldKeepNode = currentLayerTreeNode.prMarinaNode
      .getAbsoluteBounds()
      .contains(point)

    const layersListToUseAsChildren = currentLayerTreeNode.children.reduce(
      (acc: InspectorLayersTreeNode[], child) => {
        const layersListForChild = getLayersListToUseAsChildren(child)

        acc.push(...layersListForChild)

        return acc
      },
      []
    )

    if (!shouldKeepNode) {
      // We are skipping the node that was supposed to be used as a child but
      // we want to keep the potential sub nodes and attach
      // them to the parent instead of the original node.
      return layersListToUseAsChildren
    }

    return [
      // Make sure to create a copy of the node before modifying the children
      {
        ...currentLayerTreeNode,
        children: layersListToUseAsChildren,
      },
    ]
  }

  const layerRootNode = getLayersListToUseAsChildren(layersTreeRootNode)[0]

  return layerRootNode ?? null
}
